import { Button, Container, Header, SpaceBetween, Toggle } from '@amzn/awsui-components-core';
import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { CONSTANTS } from 'src/components/das-finsuite/widgets/welcome-widget/ContactList';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { numberFormatter } from 'src/utilities/CommonUtilities';
import { DETAIL_PANEL_HEIGHT, HIERARCHY_VIEW_MODE, HierarchyViewMode } from './UserMetricsConstants';
import styles from './styles.module.scss';

// ===== Props Interface =====

export interface UserMetricsUserDetailsTableProps {
  /** The filtered user details data to display in the grid */
  rowData: any[];
  /** The selected manager's name to display in the header */
  selectedManagerName: string;
  /** The application type (used in Application Hierarchy view) */
  selectedApplicationType?: string | null;
  /** Current hierarchy view mode (Manager or Application) */
  hierarchyViewMode: HierarchyViewMode;
  /** Toggle state for Application vs Sub Application view */
  isApplicationViewMode: boolean;
  /** Callback when toggle is changed */
  onToggleViewMode: (checked: boolean) => void;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Optional pagination page size (default: 10) */
  paginationPageSize?: number;
  /** Optional custom height for the grid */
  height?: string;
  /** When true, renders only the grid without the Container wrapper (for modal use) */
  hideContainer?: boolean;
  /** Callback to expose the grid ref to parent for external operations like download */
  onGridReady?: (gridRef: React.RefObject<AgGridReact>) => void;
}

// ===== Component =====

export const UserMetricsUserDetailsTable: React.FC<UserMetricsUserDetailsTableProps> = ({
  rowData,
  selectedManagerName,
  selectedApplicationType,
  hierarchyViewMode,
  isApplicationViewMode,
  onToggleViewMode,
  onClose,
  paginationPageSize = 10,
  height = DETAIL_PANEL_HEIGHT,
  hideContainer = false,
  onGridReady
}) => {
  // ===== Context =====
  const { agGridTheme, isDarkMode } = useTheme();

  // ===== Grid Reference =====
  const userDetailsGridRef = useRef<AgGridReact>(null);

  // Expose grid ref to parent when ready
  useEffect(() => {
    if (onGridReady) {
      onGridReady(userDetailsGridRef);
    }
  }, [onGridReady]);

  // ===== Pagination Options =====
  const paginationPageSizeSelector = useMemo(() => {
    return [10, 50, 100, 1000];
  }, []);

  // ===== Column Definitions =====

  /**
   * Column definitions for Application View (aggregated by user_login + application_type)
   * Does not include Sub Application column
   */
  const applicationViewColumns: ColDef[] = useMemo(
    () => [
      { field: 'team_name', headerName: 'Team', flex: 1 },
      { field: 'application_type', headerName: 'Application', flex: 1 },
      {
        field: 'user_login',
        headerName: 'User Alias',
        flex: 1,
        cellRenderer: (params: any) => {
          const userLogin = params.value;
          const isDark = params.context?.isDarkMode ?? false;
          if (!userLogin) return '';
          return (
            <a
              href={`${CONSTANTS.PHONE_TOOL_URL}${userLogin}`}
              target="_blank"
              rel="noopener noreferrer"
              className={isDark ? styles.userAliasLinkDark : styles.userAliasLinkLight}
            >
              {userLogin}
            </a>
          );
        }
      },
      { field: 'active_since', headerName: 'Active Since', flex: 1 },
      { field: 'last_active', headerName: 'Last Accessed', flex: 1 },
      {
        field: 'measure_value',
        headerName: 'View Count',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) => numberFormatter(params.value)
      },
      {
        field: 'manager_name_hierarchy_concat',
        headerName: 'Leadership Hierarchy',
        flex: 2,
        valueGetter: (params: ValueGetterParams) => {
          const hierarchy = params.data?.user_manager_name_hierarchy;
          if (Array.isArray(hierarchy)) {
            return hierarchy.join(', ');
          }
          return '';
        }
      }
    ],
    []
  );

  /**
   * Column definitions for Sub Application View (detailed view)
   * Includes Sub Application column for granular breakdown
   */
  const subApplicationViewColumns: ColDef[] = useMemo(
    () => [
      { field: 'team_name', headerName: 'Team', flex: 1 },
      { field: 'application_type', headerName: 'Application', flex: 1 },
      { field: 'application', headerName: 'Sub Application', flex: 1 },
      {
        field: 'user_login',
        headerName: 'User Alias',
        flex: 1,
        cellRenderer: (params: any) => {
          const userLogin = params.value;
          const isDark = params.context?.isDarkMode ?? false;
          if (!userLogin) return '';
          return (
            <a
              href={`${CONSTANTS.PHONE_TOOL_URL}${userLogin}`}
              target="_blank"
              rel="noopener noreferrer"
              className={isDark ? styles.userAliasLinkDark : styles.userAliasLinkLight}
            >
              {userLogin}
            </a>
          );
        }
      },
      { field: 'active_since', headerName: 'Active Since', flex: 1 },
      { field: 'last_active', headerName: 'Last Accessed', flex: 1 },
      {
        field: 'measure_value',
        headerName: 'View Count',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) => numberFormatter(params.value)
      },
      {
        field: 'manager_name_hierarchy_concat',
        headerName: 'Leadership Hierarchy',
        flex: 2,
        valueGetter: (params: ValueGetterParams) => {
          const hierarchy = params.data?.user_manager_name_hierarchy;
          if (Array.isArray(hierarchy)) {
            return hierarchy.join(', ');
          }
          return '';
        }
      }
    ],
    []
  );

  // Select column definitions based on view mode
  const columnDefs = isApplicationViewMode ? applicationViewColumns : subApplicationViewColumns;

  // ===== Default Column Definition =====
  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      sortable: true,
      filter: true,
      resizable: true
    }),
    []
  );

  // ===== Export Handler =====

  /**
   * Exports user details grid data to Excel
   */
  const onDownloadClick = useCallback(() => {
    if (userDetailsGridRef.current && userDetailsGridRef.current.api) {
      const params = {
        fileName: `User_Details_${selectedManagerName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}`,
        suppressQuotes: true
      };
      userDetailsGridRef.current.api.exportDataAsExcel(params);
    }
  }, [selectedManagerName]);

  // ===== Header Title =====
  const headerTitle = useMemo(() => {
    if (hierarchyViewMode === HIERARCHY_VIEW_MODE.APPLICATION && selectedApplicationType) {
      return `User Details for ${selectedManagerName} and ${selectedApplicationType}`;
    }
    return `User Details for ${selectedManagerName}`;
  }, [selectedManagerName, selectedApplicationType, hierarchyViewMode]);

  // ===== Render =====

  // Grid component (shared between both render modes)
  const gridComponent = (
    <div style={{ height, width: '100%' }} className={agGridTheme}>
      <AgGridReact
        key={`user-details-${isDarkMode ? 'dark' : 'light'}`}
        ref={userDetailsGridRef}
        rowData={rowData}
        pagination={true}
        paginationPageSizeSelector={paginationPageSizeSelector}
        paginationPageSize={paginationPageSize}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows={true}
        context={{ isDarkMode }}
      />
    </div>
  );

  // When hideContainer is true, render only the grid (for modal use where header is external)
  if (hideContainer) {
    return gridComponent;
  }

  // Default: render with Container wrapper
  return (
    <Container
      header={
        <Header
          actions={
            <SpaceBetween direction="horizontal" size="s">
              <Button iconName="download" onClick={onDownloadClick}>
                Download
              </Button>
              {/* <Button iconName="close" variant="icon" onClick={onClose} /> */}
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <span>{headerTitle}</span>
            <Toggle onChange={({ detail }) => onToggleViewMode(detail.checked)} checked={isApplicationViewMode}>
              {isApplicationViewMode ? 'Application' : 'Sub Application'}
            </Toggle>
          </SpaceBetween>
        </Header>
      }
    >
      {gridComponent}
    </Container>
  );
};

export default UserMetricsUserDetailsTable;
